import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

interface SuppliedItemOption {
  id: number;
  name: string;
  supplier_name: string;
}

interface Props {
  selectedSuppliedItemIds: number[];
  onSelect: (ids: number[]) => void;
}

interface SuppliedItemResponse {
  id: number;
  name: string;
  supplier: {
    name: string;
  };
}

interface Response {
  supplied_items: SuppliedItemResponse[];
}

const ArrangementSuppliedItemSelector = observer(({ selectedSuppliedItemIds, onSelect }: Props) => {
  const [options, setOptions] = useState<SuppliedItemOption[]>([]);
  const [selectedItems, setSelectedItems] = useState<SuppliedItemOption[]>([]);
  const [query, setQuery] = useState<string>('');

  const fetchSuppliedItems = useCallback(() => {
    Fetcher.get<Response>('/arrangement/supplied_items.json').then(res => {
      const items = res.supplied_items.map(item => ({
        id: item.id,
        name: item.name,
        supplier_name: item.supplier?.name || ''
      }));
      setOptions(items);
    });
  }, []);

  useEffect(() => {
    fetchSuppliedItems();
  }, [fetchSuppliedItems]);

  useEffect(() => {
    setSelectedItems(options.filter(item => selectedSuppliedItemIds.includes(item.id)));
  }, [options, selectedSuppliedItemIds]);

  return (
    <Autocomplete
      multiple
      options={options}
      getOptionLabel={o => `${o.supplier_name}：${o.name}`}
      filterOptions={(opts, state) => {
        const q: string = state.inputValue;
        return _.filter(
          opts,
          o =>
            o.name.toLocaleLowerCase().indexOf(q.toLocaleLowerCase()) > -1 ||
            o.supplier_name.toLocaleLowerCase().indexOf(q.toLocaleLowerCase()) > -1
        );
      }}
      onChange={(_, selected) => {
        onSelect(selected.map(o => o.id));
        setQuery('');
      }}
      onInputChange={(_e, value, reason) => {
        if (reason === 'input') {
          setQuery(value);
        }
      }}
      inputValue={query}
      value={selectedItems}
      size="small"
      renderInput={params => (
        <TextField {...params} variant="outlined" InputLabelProps={{ shrink: true }} size="small" />
      )}
      style={{ minWidth: '240px' }}
    />
  );
});

export default ArrangementSuppliedItemSelector;
